import { type FocusEvent, forwardRef, useState } from 'react';
import { type FieldError } from 'react-hook-form';

import { ReactComponent as CrossIcon } from 'assets/icons/1xbet/cross-icon.svg';
import { ReactComponent as InfoIcon } from 'assets/icons/1xbet/info-icon.svg';
import { ReactComponent as HideIcon } from 'assets/icons/fonbet/close-eye-icon.svg';
import { ReactComponent as FieldErrorIcon } from 'assets/icons/fonbet/field-error-icon.svg';
import { ReactComponent as ShowIcon } from 'assets/icons/fonbet/open-eye-icon.svg';
import { twMergeClsx } from 'utils/tw-with-clsx';
import { InputType, type InputRefType, type InputTypePropTypes } from 'views/shared/input-type';
import { Tooltip } from 'views/shared/tooltip';

interface InputPropTypes {
  error?: boolean | string | null | FieldError;
  label?: string;
  containerClassName?: string;
  LeftIcon?: JSX.Element | null;
  RightIcon?: JSX.Element | null;
  clear?: () => void;
  info?: string;
  showPass?: boolean;
}

const inputStyles =
  'px-3 py-[6px] md:py-[11px] bg-white outline-none h-[42px] md:h-[44px] rounded-xsmall text-base md:text-base text-black leading-[22px] border border-solid border-fonbet-gray-100 hover:border-fonbet-blue-200/50 focus:border-fonbet-blue-200';

const Input = forwardRef<InputRefType, InputPropTypes & InputTypePropTypes>((props, ref) => {
  const {
    inputClassName,
    amount,
    error,
    containerClassName,
    label,
    name,
    id,
    value,
    disabled,
    LeftIcon,
    RightIcon,
    info,
    clear,
    showPass,
    type,
  } = props;
  const [currentType, setCurrentType] = useState(type);
  const [focus, setFocus] = useState(false);

  const onFocus = (e: FocusEvent<HTMLInputElement>) => {
    if (props.onFocus) {
      props.onFocus(e);
    }

    setFocus(true);
  };

  const onBlur = () => {
    setFocus(false);
  };

  const inputClassess = twMergeClsx(
    inputStyles,
    inputClassName,
    amount && '',
    error &&
      'text-fonbet-red border-fonbet-red-64 focus:border-fonbet-red hover:border-fonbet-red-64',
    disabled && 'cursor-not-allowed',
    LeftIcon && 'pl-[50px]',
  );

  const changeInputType = () => {
    setCurrentType(currentType === 'password' ? 'tel' : 'password');
  };

  return (
    <div className={twMergeClsx('text-sz-gray-300', containerClassName)}>
      {label ? (
        <label
          className={twMergeClsx(
            'text-sm leading-[18px] font-medium mb-1.5 block whitespace-nowrap text-fonbet-gray-200',
            focus && 'text-fonbet-blue-200',
          )}
          htmlFor={name || id}
        >
          {label}
        </label>
      ) : null}
      <div className="flex items-center gap-2">
        <div className="relative w-full">
          {LeftIcon && (
            <div className="absolute left-4 inset-y-0 flex items-center justify-center">
              {LeftIcon}
            </div>
          )}
          <InputType
            ref={ref}
            {...props}
            type={currentType}
            inputClassName={inputClassess}
            onFocus={onFocus}
            onBlur={onBlur}
          />
          <div className="absolute right-1 inset-y-0 flex items-center justify-center">
            {amount && <span className="text-base font-semibold text-black font-sans">₸</span>}
            {error && (
              <div className="w-4 md:w-6 h-4 md:h-6 flex items-center justify-center">
                <FieldErrorIcon className="w-5 h-5" />
              </div>
            )}
            {value && clear && (
              <button
                className="w-4 md:w-6 h-4 md:h-6 flex items-center justify-center"
                type="button"
                onClick={clear}
              >
                <CrossIcon className="w-5 h-5 fill-fonbet-gray-400/50 hover:fill-fonbet-gray-400" />
              </button>
            )}
            {value && showPass && (
              <button
                className="w-4 md:w-6 h-4 md:h-6 mr-1"
                type="button"
                onClick={changeInputType}
              >
                {currentType === 'password' ? (
                  <ShowIcon className="w-4 md:w-5 h-4 md:h-5 fill-fonbet-gray-400/50 hover:fill-fonbet-gray-400" />
                ) : (
                  <HideIcon className="w-4 md:w-5 h-4 md:h-5 fill-fonbet-gray-400/50 hover:fill-fonbet-gray-400" />
                )}
              </button>
            )}
            {RightIcon}
          </div>
        </div>
        {info && (
          <div className="w-4 md:w-6 h-4 md:h-6">
            <Tooltip
              text={info}
              className="bg-white border border-fonbet-gray-100 rounded-xsmall px-3 py-[6px] text-[10px] leading-[14px] text-fonbet-gray-200 whitespace-pre mb-2"
            >
              <InfoIcon className="w-4 md:w-5 h-4 md:h-5" />
            </Tooltip>
          </div>
        )}
      </div>

      {typeof value === 'string' && error && typeof error === 'string' && (
        <div className="text-fonbet-red text-xs leading-3 text-left mt-1.5 lowercase whitespace-nowrap">
          {error}
        </div>
      )}
    </div>
  );
});

export { Input };
